import cn from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { EMPTY_ARRAY, generateId, formatDate, isNullish } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import Combobox from '../combobox';
import Icon from '../icon';
import InputMask from '../input-mask';
import FileList from './file-list';
import RangeValue, { setRangeCustomProperties } from './range';
import type {
  ComponentChildren,
  ComponentProps,
  Ref,
  RefCallback,
  TargetedEvent,
  TargetedInputEvent,
} from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { ColorVariant } from '../../types/common';
import type { HTMLAttributes, PickOptional, RequireAtLeastOne } from '../../types/utility';
import type { IconType } from '../../types/icons';
import type { ExtendedSize } from '../../types/size';
import type {
  CurrencyOptions,
  DateTimeOptions,
  PercentageOptions,
  PhoneOptions,
} from '../input-mask';

const CHECKABLE_FIELDS = new Set(['checkbox', 'radio']);
export const TYPES_TO_SHOW_PICKER = new Set(['datetime-local', 'date', 'time', 'month', 'week']);

const disableNumberInputScroll = (e: Event) => {
  e.preventDefault();
};

const toggleDateTimePlaceholderState = (e: Event) => {
  const currentTarget = e.currentTarget as HTMLInputElement;
  const hasValue = !!currentTarget.value;
  currentTarget.classList[hasValue ? 'add' : 'remove']('has-value');
};

const handleAutoSize = (e: TargetedEvent<HTMLTextAreaElement>) => {
  const { currentTarget } = e;

  // force reflow to get accurate scrollHeight
  currentTarget.style.height = '0';

  const { scrollHeight } = currentTarget;
  const { lineHeight, paddingTop, paddingBottom } = getComputedStyle(currentTarget);

  const rows =
    (scrollHeight - parseInt(paddingTop) - parseInt(paddingBottom)) / parseInt(lineHeight);

  currentTarget.rows = Math.ceil(rows || 1);

  currentTarget.style.height = 'auto';
};

type Badge = number | string;

export type Option = {
  label: ComponentChildren;
  value?: string;
  selected?: boolean;
  badge?: Badge;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  icon?: IconType;
  iconColor?: ColorVariant;
  subtext?: ComponentChildren; // TODO: make string or string[] instead?
  href?: string;
  target?: '_blank';
  [key: `data-${string}`]: string;
  options?: never | undefined;
  title?: string;
};

export type OptGroup = {
  label: ComponentChildren;
  disabled?: boolean;
  options: Option[];
};

export type Options = (Option | OptGroup)[] | Readonly<(Option | OptGroup)[]>;

type FormFieldLabelProps = RequireAtLeastOne<{
  'aria-label': string;
  label: ComponentChildren;
}>;

type FormFieldBaseProps = FormFieldLabelProps & {
  badge?: Badge;
  errorMessage?: ComponentChildren;
  hintMessage?: ComponentChildren;
  icon?: IconType;
  iconColor?: ColorVariant;
  name: string;
  isNative?: boolean;
  multiple?: boolean;
  inputMask?: ComponentProps<typeof InputMask>['options'];
  size?: Omit<ExtendedSize, 'x-large'>;
  /**
   * `type="file"` only. Renders the built-in `FileList` (name + remove button) below the
   * drop zone. Defaults to `true`, which is the historical behaviour for every consumer.
   *
   * Pass `false` when the consumer needs its own per-file row UI — e.g. a per-file
   * document-type dropdown. Only the ROWS are suppressed: the file field's behaviour
   * (multi-select accumulation, `accept` checking, max-file-size, min/max-files,
   * `setCustomValidity`) lives in the same component and stays active, as does everything
   * the `<input type="file">` holds, so form submission is unaffected. The consumer becomes
   * responsible for displaying the selected files and offering a way to remove them.
   */
  renderFileList?: boolean;
};

type HTMLAttributesWithoutSize<T extends EventTarget> = Omit<
  HTMLAttributes<T>,
  'size' | 'defaultValue'
> & { defaultValue?: string | string[] };

type TextAreaProps = {
  as: 'textarea';
  options?: never;
  isNative?: never;
} & HTMLAttributesWithoutSize<HTMLTextAreaElement>;

type SelectProps = {
  as: 'select';
  options: Options;
} & HTMLAttributesWithoutSize<HTMLSelectElement>;

type InputProps = {
  as?: 'input';
  options?: Options;
} & HTMLAttributesWithoutSize<HTMLInputElement>;

type InputSelectProps = {
  as: 'input-select';
  isNative: false;
  options: Options;
  multiple?: boolean;
} & HTMLAttributesWithoutSize<HTMLInputElement>;

export type FormFieldProps = FormFieldBaseProps &
  (TextAreaProps | SelectProps | InputProps | InputSelectProps);

export type CheckableProps = Omit<Exclude<FormFieldProps, SelectProps | TextAreaProps>, 'type'> & {
  label: ComponentChildren;
};

export default function FormField({
  label,
  as = 'input',
  badge,
  className,
  icon,
  iconColor,
  isNative = true,
  options = EMPTY_ARRAY,
  hintMessage,
  errorMessage,
  id: propId,
  inputMask,
  // React 19 delivers `ref` as a regular prop; keep it out of the spread so
  // it can't clobber our internal ref
  ref: forwardedRef,
  size = 'medium',
  // Destructured (not spread) so it never reaches the DOM as an unknown attribute.
  renderFileList = true,
  ...fieldProps
}: FormFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>();
  // the union collapses to whichever element this field renders
  const mergedInputRef = useMergedRef(
    inputRef,
    forwardedRef as unknown as Ref<HTMLInputElement | HTMLTextAreaElement>,
  );

  const id = useMemo(() => {
    return propId || generateId();
  }, [propId]);

  const children = useMemo(() => {
    if (!isNative) {
      return;
    }

    const renderOption = (option: Option) => {
      const { label, value, selected, disabled, ...dataset } = option;
      const keyValue = value || label;

      return (
        <option key={keyValue} value={value} selected={selected} disabled={disabled} {...dataset}>
          {label}
        </option>
      );
    };

    return options.map((option, index) => {
      if (Array.isArray(option.options)) {
        return (
          <optgroup
            key={option.label || index}
            label={option.label as string}
            disabled={option.disabled}
          >
            {option.options.map(renderOption)}
          </optgroup>
        );
      }

      return renderOption(option as Option);
    });
  }, [options, isNative]);

  useEffect(() => {
    if (inputRef.current) {
      const currentTarget = inputRef.current;
      if (TYPES_TO_SHOW_PICKER.has(currentTarget.type)) {
        currentTarget.addEventListener('click', (currentTarget as HTMLInputElement).showPicker);
        currentTarget.addEventListener('change', toggleDateTimePlaceholderState);
        toggleDateTimePlaceholderState({ currentTarget } as unknown as Event);
      }

      if (currentTarget.type === 'range') {
        currentTarget.addEventListener('input', setRangeCustomProperties as EventListener);
        currentTarget.dispatchEvent(new CustomEvent('input')); // force values to calculate on mount
      }

      if (currentTarget.type === 'number') {
        currentTarget.addEventListener('wheel', disableNumberInputScroll, { passive: false });
      }

      if (currentTarget.nodeName === 'TEXTAREA' && currentTarget.closest('.auto-size')) {
        currentTarget.addEventListener(
          'contentvisibilityautostatechange',
          handleAutoSize as EventListener,
        ); // when <textarea> is initially hidden no scrollHeight exists (e.g. when nested in <Tabs> with inactive panel)
        currentTarget.addEventListener('input', handleAutoSize as EventListener);
        currentTarget.dispatchEvent(new CustomEvent('input')); // force auto-size on mount
      }

      return () => {
        currentTarget.removeEventListener('click', (currentTarget as HTMLInputElement).showPicker);
        currentTarget.removeEventListener('change', toggleDateTimePlaceholderState);
        currentTarget.removeEventListener('input', setRangeCustomProperties as EventListener);
        // @ts-expect-error passive is a valid EventListenerOption
        currentTarget.removeEventListener('wheel', disableNumberInputScroll, { passive: false });
        currentTarget.removeEventListener('input', handleAutoSize as EventListener);
        currentTarget.removeEventListener(
          'contentvisibilityautostatechange',
          handleAutoSize as EventListener,
        );
      };
    }
  }, []);

  // `indeterminate` is a DOM property, not an HTML attribute. Preact happens
  // to forward it when used in JSX; React strips it. Sync it imperatively so
  // checkboxes display the indeterminate state correctly in both runtimes
  // (matters when BLOX is consumed by an app that aliases preact → react,
  // e.g. the HOA mail app — without this, "X selected" headers in
  // SelectableList render an empty checkbox instead of a dashed one).
  //
  // Coerce undefined → false so an unmount/remount of a different Checkbox
  // instance (e.g. SelectableList swapping its "Select all" vs "X selected"
  // headers) that reuses the same `<input>` DOM element resets the property.
  // A bare `typeof indeterminate === 'boolean'` guard would let a stale
  // `true` persist when the new render omits the prop.
  const indeterminate = (fieldProps as { indeterminate?: boolean }).indeterminate ?? false;
  // `useLayoutEffect` rather than `useEffect` so the DOM property is synced
  // before the browser paints — with plain `useEffect`, there's a one-frame
  // gap between React committing the new render and the property updating,
  // which reads as a delayed flicker when toggling indeterminate off.
  useLayoutEffect(() => {
    if (inputRef.current instanceof HTMLInputElement) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  // @ts-ignore type is available
  const { type } = fieldProps;
  const isCheckable = CHECKABLE_FIELDS.has(type ?? '');
  let errorElement: ComponentChildren;
  let hintElement: ComponentChildren;
  let iconElement: ComponentChildren;
  let labelElement: ComponentChildren;
  let dataListElement: ComponentChildren;
  let content: ComponentChildren;

  if (label) {
    const badgedLabel = !isNullish(badge) ? (
      <>
        {label}
        <span className="form-field-badge">{badge}</span>
      </>
    ) : (
      label
    );

    labelElement = (
      <label htmlFor={id}>
        {isCheckable && icon ? (
          <Icon as="div" icon={icon} iconColor={iconColor} className="form-field-label-icon">
            {badgedLabel}
          </Icon>
        ) : (
          badgedLabel
        )}
      </label>
    );
  }

  if (hintMessage) {
    const hintId = `hint-${id}`;
    fieldProps['aria-describedby'] = hintId;
    hintElement = (
      <small className="hint-message" id={hintId}>
        {hintMessage}
      </small>
    );
  }

  // we always want an error message to render for [type="file"] for custom validation
  if (errorMessage || type === 'file') {
    const errorId = `error-${id}`;
    fieldProps['aria-errormessage'] = errorId;
    errorElement = (
      <small className="error-message" id={errorId}>
        {errorMessage}
      </small>
    );
  }

  if (icon) {
    iconElement = (
      <Icon
        as="span"
        className="form-field-icon"
        icon={icon}
        iconColor={iconColor}
        aria-hidden="true"
      />
    );
  }

  if (options && options.length > 0 && as === 'input' && isNative) {
    const listId = `list-${id}`;
    (fieldProps as InputProps)['list'] = listId;
    dataListElement = <datalist id={listId}>{children}</datalist>;
  }

  (fieldProps as InputProps)['id'] = id;

  if (isNative || as === 'textarea') {
    switch (as) {
      case 'select':
        content = (
          <>
            {labelElement}
            <select
              ref={forwardedRef as unknown as Ref<HTMLSelectElement>}
              {...(fieldProps as HTMLAttributes<HTMLSelectElement>)}
            >
              {children}
            </select>
          </>
        );
        break;
      case 'textarea':
        content = (
          <>
            {labelElement}
            <textarea
              ref={mergedInputRef as unknown as RefCallback<HTMLTextAreaElement>}
              {...(fieldProps as HTMLAttributes<HTMLTextAreaElement>)}
            />
          </>
        );
        break;
      default:
        if (isCheckable) {
          content = (
            <>
              <input
                ref={mergedInputRef as unknown as RefCallback<HTMLInputElement>}
                {...(fieldProps as HTMLAttributes<HTMLInputElement>)}
              />
              {labelElement}
            </>
          );
        } else {
          let input;

          if (inputMask) {
            input = (
              <InputMask
                // thread the forwarded ref through the plain object prop —
                // a JSX-level `ref` would be intercepted by the renderer
                fieldProps={{ ...fieldProps, ref: forwardedRef } as HTMLAttributes<HTMLInputElement>}
                options={inputMask}
              />
            );
          } else {
            input = (
              <input
                ref={mergedInputRef as unknown as RefCallback<HTMLInputElement>}
                autoComplete="off"
                {...(fieldProps as HTMLAttributes<HTMLInputElement>)}
              />
            );
          }

          content = (
            <>
              {labelElement}
              {iconElement}
              {input}
              {dataListElement}
            </>
          );
        }
    }
  } else {
    if (as === 'input-select') {
      fieldProps['data-is-input-select'] = 'true';
    }

    content = (
      <>
        {labelElement}
        {iconElement}
        {/* @ts-ignore expresses a union that's too complex to represent */}
        <Combobox
          /* @ts-ignore-error Type 'string' is not assignable to type '"input" | "select"' */
          as={as}
          options={options}
          selectionType={type === 'search' ? 'automatic' : 'manual'}
          {...(fieldProps as HTMLAttributes<HTMLInputElement>)}
        />
      </>
    );
  }

  return (
    <div
      className={cn('form-field', className, {
        [`size-${size}`]: size !== 'medium',
      })}
      hidden={type === 'hidden'}
    >
      {content}
      {type === 'range' ? <RangeValue /> : null}
      {hintElement}
      {errorElement}
      {/* Always MOUNTED for a file field: `FileList` owns the change listener that does
          accumulation, accept/size/count validation and `setCustomValidity`. `renderFileList`
          only controls whether its rows are drawn. */}
      {type === 'file' ? (
        <FileList
          inputRef={inputRef as MutableRef<HTMLInputElement>}
          renderList={renderFileList}
        />
      ) : null}
    </div>
  );
}

export function Input(props: Extract<FormFieldProps, InputProps>) {
  return <FormField {...props} />;
}

export function HiddenInput({ name, value, ...props }: HTMLAttributes<HTMLInputElement>) {
  return <input type="hidden" name={name} value={value} {...props} />;
}

interface RangeInputProps {
  minLabel?: ComponentChildren;
  maxLabel?: ComponentChildren;
  formatTooltip?: (value: string) => string;
}

export function RangeInput({
  minLabel,
  maxLabel,
  formatTooltip,
  onInput,
  ...props
}: Extract<FormFieldProps, InputProps> & RangeInputProps) {
  let hintMessage;
  if (minLabel || maxLabel) {
    hintMessage = (
      <>
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </>
    );
  }

  const handleInput = useCallback(
    (e: TargetedInputEvent<HTMLInputElement>) => {
      onInput?.(e);

      if (typeof formatTooltip === 'function') {
        e.currentTarget.dataset.tooltipValue = formatTooltip(e.currentTarget.value);
      }
    },
    [onInput, formatTooltip],
  );

  return <FormField hintMessage={hintMessage} {...props} type="range" onInput={handleInput} />;
}

export function FileUpload({
  maxFiles,
  maxFileSize,
  minFiles,
  ...props
}: PickOptional<Extract<FormFieldProps, InputProps>, 'label' | 'aria-label'> & {
  maxFiles?: number;
  maxFileSize?: number;
  minFiles?: number;
}) {
  return (
    <FormField
      label={
        (
          <>
            Drag and drop <br />
            or <u>browse files</u>
          </>
        ) as unknown as string
      }
      {...props}
      data-max-files={maxFiles}
      data-max-file-size={maxFileSize}
      data-min-files={minFiles}
      type="file"
    />
  );
}

export function Textarea({
  autoSize,
  className,
  ...props
}: Omit<Extract<FormFieldProps, TextAreaProps>, 'as'> &
  FormFieldLabelProps & { autoSize?: boolean }) {
  const classList = cn(className, { ['auto-size']: autoSize });
  return <FormField {...props} as="textarea" className={classList} />;
}

export function Select(
  props: Omit<Extract<FormFieldProps, SelectProps>, 'as'> & FormFieldLabelProps,
) {
  return <FormField isNative={false} {...props} as="select" />;
}

export function MultiSelect(
  props: Omit<Extract<FormFieldProps, InputSelectProps>, 'as' | 'isNative'> &
    FormFieldLabelProps & { collapseOptions?: boolean },
) {
  return <FormField {...props} as="input-select" multiple={true} isNative={false} />;
}

export function SearchableSelect(
  props: Omit<Extract<FormFieldProps, InputSelectProps>, 'as' | 'isNative'> & FormFieldLabelProps,
) {
  return <FormField {...props} as="input-select" isNative={false} />;
}

export function CurrencyInput({
  allowNegative,
  disableCents,
  output,
  ...props
}: FormFieldProps & Omit<CurrencyOptions, 'type'>) {
  return (
    <FormField {...props} inputMask={{ type: 'currency', allowNegative, disableCents, output }} />
  );
}

export function PercentageInput({
  maximumFractionDigits,
  output,
  ...props
}: FormFieldProps & Omit<PercentageOptions, 'type'>) {
  return <FormField {...props} inputMask={{ type: 'percentage', maximumFractionDigits, output }} />;
}

export function PhoneInput({ output, ...props }: FormFieldProps & Omit<PhoneOptions, 'type'>) {
  return (
    // @ts-expect-error expression produces too large of a union
    <FormField pattern="\(\d{3}\) \d{3}-\d{4}" {...props} inputMask={{ type: 'phone', output }} />
  );
}

export function Checkbox({ children, ...props }: CheckableProps) {
  return (
    <FormField {...props} as="input" type="checkbox">
      {children}
    </FormField>
  );
}

export function RadioButton({ children, ...props }: CheckableProps) {
  return (
    <FormField {...props} as="input" type="radio">
      {children}
    </FormField>
  );
}

const IANA_TIME_ZONES = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Phoenix': 'Arizona Time',
};

export function DateTimeInput({
  defaultValue: passedDefaultValue,
  timeZone = '',
  ...props
}: Omit<Exclude<FormFieldProps, SelectProps | TextAreaProps>, 'defaultValue'> &
  Omit<DateTimeOptions, 'type'> & { defaultValue?: number | string | Date }) {
  const [date, setDate] = useState<Date | undefined>(() => {
    return passedDefaultValue ? new Date(passedDefaultValue as string) : undefined;
  });

  const defaultValue = useMemo(() => {
    return formatDate(date, { type: 'iso-local', format: 'YYYY-MM-DDThh:mm' }, timeZone);
  }, [date, timeZone]);

  const handleInput = useCallback((e: TargetedInputEvent<HTMLInputElement>) => {
    const { currentTarget } = e;

    const utc = (currentTarget.nextElementSibling as HTMLInputElement).value;
    setDate(utc ? new Date(utc) : undefined);
  }, []);

  return (
    // @ts-expect-error TS2590 - union type too complex for declaration emit
    <FormField
      {...props}
      defaultValue={defaultValue}
      type="datetime-local"
      inputMask={{ type: 'datetime', timeZone }}
      // @ts-ignore label can be ComponentChildren
      label={
        <>
          {props.label}
          {!timeZone ? null : (
            <span className="content-secondary label-small">
              {' '}
              &bull; {IANA_TIME_ZONES[timeZone] || timeZone}
            </span>
          )}
        </>
      }
      hintMessage={
        timeZone && date
          ? `This is ${formatDate(date, 'MM/DD/YYYY, HH:MI TZ')} (your local time)`
          : ''
      }
      onInput={handleInput}
    />
  );
}
